// App.js
// Updated to support custom URL Paths (url_path) for articles
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import { UserAuthProvider } from './UserAuthContext';
import { AdminAuthProvider } from './AdminAuthContext';
import { ControlAuthProvider } from './ControlAuthContext';
import { LanguageProvider } from './LanguageContext';

// Componentes 
import Header from './components/Secciones/Header';
import Footer from './components/Secciones/Footer';
import HomePage from './components/HomePage/HomePage';

// Links
import Signup from './pages/Signup';
import SearchResults from './pages/SearchResults';
import PublicProductDetails from './pages/PublicProductDetails';
import Cart from './pages/Cart';
import Orders from './pages/Orders';
import Checkout from './pages/Checkout';
import UserOrderDetails from './pages/UserOrdenDetails';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPasswordForm from './pages/ForgotPassword';
import ResetPasswordForm from './pages/ResetPassword';
import Terms from './pages/Terms';
import PrivacyPolicy from './pages/PrivacyPolicy';
import BrandsPage from './pages/BrandsPage';
import CompanyPage from './pages/CompanyPage';
import AutomationInfo from './pages/AutomationInfo';
import NotFound from './pages/NotFound';
// NOTE: The public feed (/feed, /blogs, /services, /products-news and article
// detail pages) is now served as a static Astro site from `feed-astro/`,
// deployed to the same web root. Those React routes were removed so the server
// serves the Astro pages directly. The old page components (Feed, Blogs,
// Services, ProductsNews, ArticleDetail) remain in src/pages/ but are unused.
import BrandResults from './pages/BrandResults';
import ThankYou from './pages/ThankYou';
import SeoManager from './components/SeoManager';
import ScrollToTop from './components/ScrollToTop';
import SitePopup from './components/SitePopup';

// Admin Links
import AdminPanel from './pages/admin/AdminPanel';
import { AdminProducts } from './pages/admin/AdminProducts';
import AdminBrands from './pages/admin/AdminBrands';
import AdminProductDetails from './pages/admin/AdminProductDetails';
import AdminLayout from './admin_components/AdminLayout';
import Companies from './pages/admin/Companies';
import AdminCountries from './pages/admin/AdminCountries';
import AdminLogin from './pages/admin/AdminLogin';
import AdminRoute from './admin_components/AdminRoute';
import UserAccounts from './pages/admin/UserAccounts';
import AdminAdd from './pages/admin/AdminAdd';
import AddUserForm from './pages/admin/AddUserForm';
import EditUserForm from './pages/admin/EditUserForm';
import AdminOrders from './pages/admin/AdminOrders';
import OrderDetails from './pages/admin/OrderDetails';
import AddCountryForm from './pages/admin/AddCountryForm';
import Batches from './pages/admin/Batches';
import AddCSV from './pages/admin/AddCSV';
import SpecialFees from './pages/admin/SpecialFees';
import CreateOrder from './pages/admin/CreateOrder';
import Content from './pages/admin/Content';

// Control Links
import ControlLayout from './control_components/ControlLayout';
import ControlRoute from './control_components/ControlRoute';
import ControlLogin from './pages/control/ControlLogin';
import ControlPanel from './pages/control/ControlPanel';
import OrdersControl from './pages/control/OrdersControl';
import ControlCreateOrder from './pages/control/ControlCreateOrder';
import UserControl from './control_components/UserControl';
import CreateUser from './control_components/CreateUser';
import ControlOrderDetails from './pages/control/ControlOrderDetails';
import PendingQuotes from './pages/control/PendingQuotes'; 
import ControlEditOrder from './pages/control/ControlEditOrder';
import CreateQuote from './pages/control/CreateQuote'
import EditQuote from './pages/control/EditQuote';

const Home = () => (
  <div className="site-shell">
    <Header />
    <HomePage />
    <Footer />
  </div>
);

const App = () => {
  return (
    <LanguageProvider>
      <Router>
        <UserAuthProvider>
        <AdminAuthProvider>
          <ControlAuthProvider>
            <SeoManager />
            <ScrollToTop />
            <SitePopup />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Home />} />
              <Route path="/signup" element={<Signup />} />
              {/* /feed and /articles/:url_path are served by the Astro feed site */}
              <Route path="/about" element={<CompanyPage />} />
              <Route path="/search" element={<SearchResults />} />
              <Route path="/brands" element={<BrandsPage />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/products/:product_name" element={<PublicProductDetails />} />
              <Route path="/admin-login" element={<AdminLogin />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/orders/:id" element={<UserOrderDetails />} />
              <Route path="/checkout/:id" element={<Checkout />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPasswordForm />} />
              <Route path="/reset-password" element={<ResetPasswordForm />} />
              <Route path="/info" element={<AutomationInfo />} />
              <Route path="/thank-you" element={<ThankYou />} />
              {/* /services, /products-news, /blogs are served by the Astro feed site */}
              <Route path="/brands/:idbrand" element={<BrandResults />} />
              <Route path="*" element={<NotFound />} />

              {/* Admin Protected Routes */}
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminLayout><AdminPanel /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/orders" element={
                <AdminRoute>
                  <AdminLayout><AdminOrders /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/orders/create" element={
                <AdminRoute>
                  <AdminLayout><CreateOrder /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/orders/:id" element={
                <AdminRoute>
                  <AdminLayout><OrderDetails /></AdminLayout>
                </AdminRoute>
              } /> 
              <Route path="/admin/companies" element={
                <AdminRoute>
                  <AdminLayout><Companies /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/content" element={
                <AdminRoute>
                  <AdminLayout><Content /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/companies/edit/:userId" element={
                <AdminRoute>
                  <AdminLayout><EditUserForm /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/products" element={
                <AdminRoute>
                  <AdminLayout><AdminProducts /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/brands" element={
                <AdminRoute>
                  <AdminLayout><AdminBrands /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/products/add" element={
                <AdminRoute>
                  <AdminLayout><AdminAdd /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/products/addcsv" element={
                <AdminRoute>
                  <AdminLayout><AddCSV /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/products/:product_name" element={
                <AdminRoute>
                  <AdminLayout><AdminProductDetails /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/countries" element={
                <AdminRoute>
                  <AdminLayout><AdminCountries /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/countries/add" element={
                <AdminRoute>
                  <AdminLayout><AddCountryForm /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/user-accounts" element={
                <AdminRoute>
                  <AdminLayout><UserAccounts /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/companies/add" element={
                <AdminRoute>
                  <AdminLayout><AddUserForm /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/batches" element={
                <AdminRoute>
                  <AdminLayout><Batches /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/batches/:batchId" element={
                <AdminRoute>
                  <AdminLayout><Batches /></AdminLayout>
                </AdminRoute>
              } />
              <Route path="/admin/fees" element={
                <AdminRoute>
                  <AdminLayout><SpecialFees /></AdminLayout>
                </AdminRoute>
              } />

              {/* Control Routes */}
              <Route path="/control-login" element={<ControlLogin />} />
              <Route path="/control" element={
                <ControlRoute>
                  <ControlLayout><ControlPanel /></ControlLayout>
                </ControlRoute>
              } />
              <Route path="/control/orders-control" element={
                <ControlRoute>
                  <ControlLayout><OrdersControl /></ControlLayout>
                </ControlRoute>
              } />
              <Route path="/control/orders-control/create" element={
                <ControlRoute>
                  <ControlLayout><ControlCreateOrder /></ControlLayout>
                </ControlRoute>
              } />
              <Route path="/control/orders-control/:orderNumber" element={
                <ControlRoute>
                  <ControlLayout><ControlOrderDetails /></ControlLayout>
                </ControlRoute>
              } />
              <Route path="/control/pending-quotes/create" element={
                <ControlRoute>
                  <ControlLayout><CreateQuote /></ControlLayout>
                </ControlRoute>
              } />
              <Route path="/control/pending-quotes/edit/:id" element={
                <ControlRoute>
                  <ControlLayout><EditQuote /></ControlLayout>
                </ControlRoute>
              } />
              <Route path="/control/pending-quotes" element={ 
                <ControlRoute>
                  <ControlLayout><PendingQuotes /></ControlLayout>
                </ControlRoute>
              } />
              <Route path="/control/user-control" element={
                <ControlRoute>
                  <ControlLayout><UserControl /></ControlLayout>
                </ControlRoute>
              } />
              <Route path="/control/user-control/create" element={
                <ControlRoute>
                  <ControlLayout><CreateUser /></ControlLayout>
                </ControlRoute>
              } />
              <Route path="/control/orders-control/edit/:id" element={
                <ControlRoute>
                  <ControlLayout><ControlEditOrder /></ControlLayout>
                </ControlRoute>
              } />

            </Routes>
            <ToastContainer
              position="top-right"
              autoClose={2000}
              closeOnClick
              pauseOnHover={false}
              draggable
              newestOnTop
            />
          </ControlAuthProvider>
        </AdminAuthProvider>
        </UserAuthProvider>
      </Router>
    </LanguageProvider>
  );
};

export default App;
